import { Component, OnInit, OnDestroy, Input, ViewChild, OnChanges, SimpleChanges, ChangeDetectionStrategy, ChangeDetectorRef } from '@angular/core';
import { DatePipe } from '@angular/common';
import { Chart } from 'chart.js';
import { TranslateService } from '@ngx-translate/core';

import { zoneOffset } from '../../zoned-time';

// Chart.js v2 plugin for rounded top bars
const roundedBarPlugin = {
  afterDatasetsDraw: function(chart) {
    const ctx = chart.ctx;
    chart.data.datasets.forEach(function(dataset, datasetIndex) {
      const meta = chart.getDatasetMeta(datasetIndex);
      if (meta.type !== 'bar') return;
      meta.data.forEach(function(bar, index) {
        const model = bar._model;
        if (!model || model.base === model.y) return;
        const radius = 4;
        const { x, y, base, width } = model;
        const left = x - width / 2;
        const right = x + width / 2;
        ctx.save();
        ctx.beginPath();
        ctx.moveTo(left, base);
        ctx.lineTo(left, y + radius);
        ctx.quadraticCurveTo(left, y, left + radius, y);
        ctx.lineTo(right - radius, y);
        ctx.quadraticCurveTo(right, y, right, y + radius);
        ctx.lineTo(right, base);
        ctx.closePath();
        ctx.fillStyle = model.backgroundColor;
        ctx.fill();
        ctx.restore();
      });
    });
  }
};

Chart.pluginService.register(roundedBarPlugin);

// Pionowe przerywane linie na granicach dni (włączane przez options.daySeparators).
// Granicę wykrywamy po zmianie daty (prefiks "yyyy-MM-dd") między kolejnymi etykietami.
const daySeparatorPlugin = {
  afterDraw: function(chart) {
    if (!chart.options || !chart.options.daySeparators) { return; }
    const meta = chart.getDatasetMeta(0);
    if (!meta || !meta.data || meta.data.length < 2) { return; }
    const labels = chart.data.labels || [];
    const yAxis = chart.scales['y-axis-0'];
    if (!yAxis) { return; }
    const dayOf = (lbl) => (typeof lbl === 'string' ? lbl.trim().slice(0, 10) : '');
    const dark = typeof document !== 'undefined' && document.body.classList.contains('dark-mode');
    const ctx = chart.ctx;
    ctx.save();
    ctx.strokeStyle = dark ? 'rgba(255,255,255,0.22)' : 'rgba(0,0,0,0.16)';
    ctx.lineWidth = 1;
    ctx.setLineDash([4, 4]);
    for (let i = 1; i < meta.data.length; i++) {
      if (dayOf(labels[i]) !== dayOf(labels[i - 1])) {
        const mPrev = meta.data[i - 1]._model;
        const mCur = meta.data[i]._model;
        if (!mPrev || !mCur) { continue; }
        const x = (mPrev.x + mCur.x) / 2;
        ctx.beginPath();
        ctx.moveTo(x, yAxis.top);
        ctx.lineTo(x, yAxis.bottom);
        ctx.stroke();
      }
    }
    ctx.restore();
  }
};
Chart.pluginService.register(daySeparatorPlugin);

@Component({
  selector: 'app-diagram',
  templateUrl: './diagram.component.html',
  styleUrls: ['./diagram.component.scss'],
  // OnPush: wykres przelicza się tylko przy zmianie @Input / zdarzeniach / markForCheck.
  // Stan ustawiany asynchronicznie (yTicks, szerokość) wymaga markForCheck — patrz updateYTicks().
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class DiagramComponent implements OnInit, OnChanges, OnDestroy {

  @Input() type;
  @Input() data;
  @Input() scaleFrom;
  @Input() scaleTo;
  @Input() format = 'yyyy-MM-dd HH:mm';
  @Input() daySeparators = false;
  @Input() showConfidenceLegend = true;
  @Input() timeZone: string;
  @ViewChild('canvas', { static: true }) canvas;
  public chart;
  public chartWidthPx = 0;
  public noData = false;
  public yTicks: {value: string, y: number}[] = [];
  public legendItems: {level: number, label: string, color: string, hidden: boolean}[] = [];
  private hiddenLevels: Set<number> = new Set();
  private readonly MIN_BAR_WIDTH = 8;

  private colorSchemes = {
    pulse: {
      high: 'rgba(197, 102, 102, 1)',
      medium: 'rgba(197, 102, 102, 0.6)',
      low: 'rgba(197, 102, 102, 0.25)',
      border: 'rgba(197, 102, 102, 0.62)'
    },
    sdnn: {
      high: 'rgba(234, 179, 102, 1)',
      medium: 'rgba(234, 179, 102, 0.6)',
      low: 'rgba(234, 179, 102, 0.25)',
      border: 'rgba(178, 148, 102, 1)'
    },
    rmssd: {
      high: 'rgba(234, 179, 102, 1)',
      medium: 'rgba(234, 179, 102, 0.6)',
      low: 'rgba(234, 179, 102, 0.25)',
      border: 'rgba(178, 148, 102, 1)'
    },
    sat: {
      high: 'rgba(102, 163, 197, 1)',
      medium: 'rgba(102, 163, 197, 0.6)',
      low: 'rgba(102, 163, 197, 0.25)',
      border: 'rgba(102, 163, 197, 0.62)'
    },
    steps: {
      high: 'rgba(102, 163, 102, 1)',
      medium: 'rgba(102, 163, 102, 0.6)',
      low: 'rgba(102, 163, 102, 0.25)',
      border: 'rgba(102, 163, 102, 0.62)'
    },
    sleep: {
      high: 'rgba(163, 102, 197, 1)',
      medium: 'rgba(163, 102, 197, 0.6)',
      low: 'rgba(163, 102, 197, 0.25)',
      border: 'rgba(163, 102, 197, 0.62)'
    },
    // Ruch / aktywność ruchowa — renderowany jako wykres LINIOWY (nie słupkowy)
    movement: {
      high: 'rgba(102, 163, 102, 1)',
      medium: 'rgba(102, 163, 102, 0.6)',
      low: 'rgba(102, 163, 102, 0.25)',
      border: 'rgba(102, 163, 102, 1)'
    }
  };

  // Typy renderowane jako wykres liniowy zamiast słupkowego.
  private get isLineChart(): boolean {
    return this.type === 'movement';
  }

  constructor(private datePipe: DatePipe, private translate: TranslateService, private cdr: ChangeDetectorRef) { }

  // Bez legendy odcienie pewności niczego nie tłumaczą, więc taki wykres (kroki) ma jeden kolor.
  private barColor(point: any, colors: any): string {
    if (!this.showConfidenceLegend || point.trust_lvl === 3) return colors.high;
    if (point.trust_lvl === 2) return colors.medium;
    return colors.low;
  }

  // Buduje dataset zależnie od trybu: liniowy (movement) lub słupkowy (reszta).
  private buildDataset(dataArr: any[], colors: any): any {
    if (this.isLineChart) {
      return {
        data: dataArr.map(d => d.y),
        fill: false,
        borderColor: colors.border,
        backgroundColor: colors.border,
        pointBackgroundColor: dataArr.map(() => colors.high),
        pointBorderColor: colors.border,
        pointRadius: 3,
        pointHoverRadius: 5,
        borderWidth: 2,
        lineTension: 0.35,
        label: '',
      } as any;
    }
    return {
      data: dataArr.map(d => d.y),
      backgroundColor: dataArr.map(d => this.barColor(d, colors)),
      borderWidth: 0,
      borderColor: colors.border,
      maxBarThickness: 25,
      barPercentage: 0.5,
      label: '',
    } as any;
  }

  private initLegend() {
    const colors = this.colorSchemes[this.type] || this.colorSchemes.pulse;
    this.legendItems = [
      { level: 3, label: this.translate.instant('COMMON.HIGH_CONFIDENCE'), color: colors.high, hidden: false },
      { level: 2, label: this.translate.instant('COMMON.MEDIUM_CONFIDENCE'), color: colors.medium, hidden: false },
      { level: 1, label: this.translate.instant('COMMON.LOW_CONFIDENCE'), color: colors.low, hidden: false }
    ];
  }

  toggleTrustLevel(level: number) {
    const item = this.legendItems.find(i => i.level === level);
    if (!item) return;
    item.hidden = !item.hidden;

    if (item.hidden) {
      this.hiddenLevels.add(level);
    } else {
      this.hiddenLevels.delete(level);
    }

    this.applyTrustFilter();
  }

  private applyTrustFilter() {
    if (!this.chart) return;
    const colors = this.colorSchemes[this.type] || this.colorSchemes.pulse;
    const currentData = this.data;

    this.chart.data.datasets[0].backgroundColor = currentData.map(d =>
      this.hiddenLevels.has(d.trust_lvl || 1) ? 'transparent' : this.barColor(d, colors));

    this.chart.data.datasets[0].data = currentData.map(d => {
      const lvl = d.trust_lvl || 1;
      if (this.hiddenLevels.has(lvl)) return null;
      return d.y;
    });

    this.chart.update();
    setTimeout(() => this.updateYTicks());
  }

  private updateYTicks() {
    if (!this.chart) return;
    const scale = this.chart.scales['y-axis-0'];
    if (!scale) return;
    this.yTicks = scale.ticks.map((value, index) => ({
      value: '' + value,
      y: scale.getPixelForTick(index)
    }));
    // Ustawiane w setTimeout (async) → pod OnPush trzeba ręcznie oznaczyć do sprawdzenia,
    // inaczej nakładka osi Y nie zaktualizowałaby się w szablonie.
    this.cdr.markForCheck();
  }

  private calcWidth() {
    const containerWidth = this.canvas.nativeElement.parentElement.parentElement.offsetWidth || 800;
    const dataLength = (this.data && this.data.length) || 0;
    const neededWidth = dataLength * this.MIN_BAR_WIDTH;
    this.chartWidthPx = neededWidth > containerWidth ? neededWidth : 0;
  }

  ngOnInit() {

    this.calcWidth();

    const colors = this.colorSchemes[this.type] || this.colorSchemes.pulse;

    this.noData = !this.data || this.data.length === 0;
    this.initLegend();

    this.chart = new Chart(this.canvas.nativeElement, {
      type: this.isLineChart ? 'line' : 'bar',
      data: {
        labels: this.data.map(data => this.label(data.x)),
        datasets: [this.buildDataset(this.data, colors)]
      },
      options: {
        scales: {
          xAxes: [{
            ticks: {
              fontSize: 14,
            },
            gridLines: {
              display: false
            }
          } as any],
          yAxes: [{
            position: 'right',
            ticks: {
              beginAtZero: true,
              fontSize: 14,
              display: false
            },
            gridLines: {
              drawBorder: false
            }
          }]
        },
        tooltips: {
          backgroundColor: '#f2f2f2',
          titleFontColor: '#6e6e6e',
          bodyFontSize: 30,
          bodyFontStyle: 'bold',
          bodyFontColor: '#000000',
          xPadding: 20,
          yPadding: 20,
          displayColors: false,
          borderColor: '#e0e0e0',
          borderWidth: 1
        },
        responsive: true,
        maintainAspectRatio: false,
        legend: {
          display: false
        },
        title: {
          display: true,
          text: '',
          fontSize: 16
        }
      }
    });

    (this.chart.options as any).daySeparators = this.daySeparators;

    setTimeout(() => this.updateYTicks());
  }

  ngOnChanges(changes: SimpleChanges) {

    this.calcWidth();

    if (!this.chart) {
      return;
    }

    (this.chart.options as any).daySeparators = this.daySeparators;
    if (changes.daySeparators && !changes.data) {
      this.chart.update();
    }

    if (changes.data && changes.data.currentValue) {
      this.noData = !changes.data.currentValue || changes.data.currentValue.length === 0;
      const colors = this.colorSchemes[this.type] || this.colorSchemes.pulse;

      this.chart.data.labels = changes.data.currentValue.map(data => this.label(data.x));
      if (this.isLineChart) {
        this.chart.data.datasets[0].data = changes.data.currentValue.map(data => data.y);
        this.chart.data.datasets[0].pointBackgroundColor = changes.data.currentValue.map(() => colors.high);
        this.chart.data.datasets[0].borderColor = colors.border;
      } else {
        this.chart.data.datasets[0].data = changes.data.currentValue.map(data => data.y);
        this.chart.data.datasets[0].backgroundColor = changes.data.currentValue.map(data => this.barColor(data, colors));
        this.chart.data.datasets[0].borderColor = colors.border;
      }

      this.chart.update();
      setTimeout(() => this.updateYTicks());
    }
  }

  private label(seconds: number): string {
    return this.datePipe.transform(seconds * 1000, this.format, zoneOffset(seconds * 1000, this.timeZone)) + '  ';
  }

  // Przełączanie zakładek analizy niszczy i tworzy ten komponent od nowa.
  // Bez destroy() instancja Chart.js zostaje w Chart.instances razem ze swoim
  // detektorem rozmiaru i nasłuchami na (już odpiętym) canvasie — po kilku
  // przełączeniach zbiera się ich kilkanaście.
  ngOnDestroy() {
    if (this.chart) {
      this.chart.destroy();
      this.chart = null;
    }
  }

}
